<template><div><h2 id="layout-stops" tabindex="-1" class="d-docsite--header-2">Layout stops <a class="header-anchor d-link d-docsite--link d-link" href="#layout-stops" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-h-{stop}</code> to set a fixed height for an element using layout token stops. The hyphen before the number indicates a layout token reference, e.g. <code v-pre>d-h-100</code> outputs <code v-pre>block-size: var(--dt-layout-100)</code> (64px).</p>
<dt-notice kind="info" title="Scale-indexed vs pixel-indexed stops" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Bare integer stops (<code v-pre>25</code>, <code v-pre>50</code>, <code v-pre>100</code>, …) are scale-indexed on the 64px base — <code v-pre>value_in_px = stop × 64 / 100</code>, so <code v-pre>25</code> = 16px and <code v-pre>100</code> = 64px. Stops with a <code v-pre>px</code> suffix (<code v-pre>1px</code>, <code v-pre>2px</code>, <code v-pre>8px</code>, <code v-pre>20px</code>, <code v-pre>24px</code>) are off-scale exceptions that encode the literal pixel value.
</dt-notice>
<code-example class="d-d-block d-bgc-secondary d-w100p d-hmn-400" source-code='&lt;div class=&quot;d-h-100&quot;&gt;...&lt;/div&gt;  &lt;!-- block-size: var(--dt-layout-100) = 64px / 6.4rem --&gt;
&lt;div class=&quot;d-h-200&quot;&gt;...&lt;/div&gt;  &lt;!-- block-size: var(--dt-layout-200) = 128px / 12.8rem --&gt;
&lt;div class=&quot;d-h-400&quot;&gt;...&lt;/div&gt;  &lt;!-- block-size: var(--dt-layout-400) = 256px / 25.6rem --&gt;'>
<div v-dt-scrollbar:never class="d-bar-400 d-d-flex d-g-200 d-bgc-secondary d-hmx-800">
  <dt-stack direction="row" align="start" gap="200">
    <dt-stack gap="100" v-for="(i, index) in layout" :key="index" v-dt-tooltip="{ message: `${i.px}px`, delay: false }">
      <dt-text kind="code" size="100" class="d-us-all">d-h-{{i.stop}}</dt-text>
      <dt-stack direction="row" align="center" justify="center" class="d-w-100 d-bgc-bold d-bar-300" :class="`d-h-${i.stop}`"></dt-stack>
    </dt-stack>
  </dt-stack>
</div>
</code-example>
<h2 id="percentages" tabindex="-1" class="d-docsite--header-2">Percentages <a class="header-anchor d-link d-docsite--link d-link" href="#percentages" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-h{n}p</code> to set a percentage height for an element. No hyphen before the number, <code v-pre>p</code> suffix indicates a literal percentage value. Note: <code v-pre>d-h33p</code> = 33.333% and <code v-pre>d-h66p</code> = 66.667%.</p>
<code-example source-code='&lt;div class=&quot;d-h-350&quot;&gt;
  &lt;div class=&quot;d-h25p&quot;&gt;25%&lt;/div&gt;
  &lt;div class=&quot;d-h50p&quot;&gt;50%&lt;/div&gt;
  &lt;div class=&quot;d-h75p&quot;&gt;75%&lt;/div&gt;
  &lt;div class=&quot;d-h100p&quot;&gt;100%&lt;/div&gt;
&lt;/div&gt;'>
<div v-dt-scrollbar:never class="d-bar-400 d-d-flex d-g-200 d-bgc-secondary d-h-500 d-ta-center">
  <dt-stack direction="row" align="center" justify="center" class="d-h100p d-ps-relative" v-for="i in percentage" v-dt-tooltip="{ message: `${i}%`, delay: false }">
    <dt-text kind="code" size="100" class="d-zi-active d-w-100 d-us-all">d-h{{i}}p</dt-text>
    <div class="d-w-100 d-h-350 d-ps-absolute d-bgc-moderate">
      <div class="d-w-100 d-bgc-bold d-bar-300" :class="`d-h${i}p`"></div>
    </div>
  </dt-stack>
</div>
</code-example>
<h2 id="viewport" tabindex="-1" class="d-docsite--header-2">Viewport <a class="header-anchor d-link d-docsite--link d-link" href="#viewport" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use viewport height utilities to size an element relative to the viewport. <code v-pre>d-h100vh</code> (no hyphen, literal CSS value) sets <code v-pre>block-size: 100vh</code>. The dynamic viewport variants use a hyphen: <code v-pre>d-h-dvh</code>, <code v-pre>d-h-svh</code>, <code v-pre>d-h-lvh</code>.</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><strong>dvh</strong> (dynamic) -- adapts to mobile browser chrome appearing/disappearing.</li>
<li class="d-docsite--list-element"><strong>svh</strong> (small) -- minimum viewport size, when mobile browser chrome is visible.</li>
<li class="d-docsite--list-element"><strong>lvh</strong> (large) -- maximum viewport size, when mobile browser chrome is hidden.</li>
</ul>
<code-example only-show="code" source-code='&lt;div class=&quot;d-h100vh&quot;&gt;...&lt;/div&gt;   &lt;!-- block-size: 100vh --&gt;
&lt;div class=&quot;d-h-dvh&quot;&gt;...&lt;/div&gt;    &lt;!-- block-size: 100dvh --&gt;
&lt;div class=&quot;d-h-svh&quot;&gt;...&lt;/div&gt;    &lt;!-- block-size: 100svh --&gt;
&lt;div class=&quot;d-h-lvh&quot;&gt;...&lt;/div&gt;    &lt;!-- block-size: 100lvh --&gt;'>
<div class="d-h100vh">...</div>   <!-- block-size: 100vh -->
<div class="d-h-dvh">...</div>    <!-- block-size: 100dvh -->
<div class="d-h-svh">...</div>    <!-- block-size: 100svh -->
<div class="d-h-lvh">...</div>    <!-- block-size: 100lvh -->
</code-example>
<h2 id="keywords" tabindex="-1" class="d-docsite--header-2">Keywords <a class="header-anchor d-link d-docsite--link d-link" href="#keywords" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use keyword utilities to set height using CSS keyword values.</p>
<code-example only-show="code" source-code='&lt;div class=&quot;d-h-auto&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-h-unset&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-h-fit-content&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-h-max-content&quot;&gt;...&lt;/div&gt;
&lt;div class=&quot;d-h-min-content&quot;&gt;...&lt;/div&gt;'>
<div class="d-h-auto">...</div>
<div class="d-h-unset">...</div>
<div class="d-h-fit-content">...</div>
<div class="d-h-max-content">...</div>
<div class="d-h-min-content">...</div>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-ibs-0">
        <tr>
          <th scope="col" class="d-p-0 d-bbw0 d-w30p"><div class="d-p-200 d-bb d-bbw1">Class</div></th>
          <th scope="col" class="d-p-0 d-bbw0"><div class="d-p-200 d-bb d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="i in layout">
          <th scope="row" class="d-code--sm d-docsite-code">.d-h-{{ i.stop }}</th>
          <td class="d-code--sm">block-size: var(--dt-layout-{{ i.stop }}) !important; <span class="d-fc-tertiary">/* {{ i.px }}px / {{ (i.px / 10).toFixed(1) }}rem */</span></td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in percentage">
          <th scope="row" class="d-code--sm d-docsite-code">.d-h{{ i }}p</th>
          <td class="d-code--sm">block-size: {{ i === 33 ? '33.333' : i === 66 ? '66.667' : i }}% !important;</td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in viewport.height">
          <th scope="row" class="d-code--sm d-docsite-code">.d-h{{ i.includes('vh') ? i : `-${i}` }}</th>
          <td class="d-code--sm">block-size: {{ i.includes('vh') ? i : `100${i}` }} !important;</td>
        </tr>
      </tbody>
      <tbody>
        <tr v-for="i in keywords">
          <th scope="row" class="d-code--sm d-docsite-code">.d-h-{{ i }}</th>
          <td class="d-code--sm">block-size: {{ i }} !important;</td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
</div></template>


<script setup>
  import { percentage, layout, viewport, keywords } from '@data/width-height.json';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
</script>